<script setup>
// The legend of a booking grid (X47; Grant 2026-10-19). Every entry is the
// grid's own cell component in that state (so it matches the cells exactly)
// with its word and one plain sentence, in a TlDialog. The words live in
// utils/bookingLegend so they never differ. Reusable: pass another `items`
// list (the admin calendar, X49) and, if its cells are another component, a
// `swatch` slot that draws one entry's swatch.
//
// X91 (Grant 2026-10-21: "I think the legend should always be in a dialog
// accessible via the top left cell"): on both booking grids (/book and the
// admin calendar), at every width, the legend is ONE TlIconBtn ("What the
// colours mean") that the grid puts in its top-left corner cell, over the
// time column; it opens the dialog. The old row of entries, the "What do these
// mean?" link and X88's phone-only pill are gone. `inline` keeps the row of
// entries (each with its tooltip, and the link to the dialog) for the hours
// painter on the Booking rules tab, which is not a booking grid and has no
// corner cell.
//
//   <booking-legend :items="MEMBER_LEGEND"/>                       (in a grid's corner)
//   <booking-legend inline :items="…"><template #swatch="{entry}">…</template></booking-legend>
import {onMounted, ref} from 'vue';
import TlDialog from '~/components/TlDialog.vue';
import TlIconBtn from '~/components/admin/TlIconBtn.vue';
import BookingCell from '~/components/bookings/BookingCell.vue';
import {LEGEND_HELP, LEGEND_TITLE} from '~/utils/bookingLegend';

defineProps({
  /** [{key, cls, word, sentence, icon?, chip?}]; X88: an entry with `chip` ('brand' | 'warn') is a block's chip (the
   *  admin calendar's Admin / over-the-rules), its swatch the chip's icon in that colour. */
  items: {type: Array, default: () => []},
  /** The dialog's heading, and the corner button's tooltip. */
  title: {type: String, default: LEGEND_TITLE},
  /** X91: the row of entries (the hours painter); off, the legend is the corner button alone. */
  inline: {type: Boolean, default: false},
});

const open = ref(false);
const touch = ref(false);
onMounted(() => {
  try { touch.value = !!window.matchMedia?.('(hover: none)')?.matches; } catch { touch.value = false; }
});
const show = () => { open.value = true; };
const onEntry = () => { if (touch.value) show(); };
// The dialog's sentence after "Word:", starting with a capital (D2 L13).
const afterWord = (l) => { const rest = l.sentence.slice(l.word.length + 1).trim(); return rest.charAt(0).toUpperCase() + rest.slice(1); };
</script>

<template>
  <div class="bl" :class="inline ? 'bl--inline' : 'bl--corner'">
    <template v-if="inline">
      <template v-for="l in items" :key="l.key">
        <v-tooltip :text="l.sentence" location="top" :disabled="touch">
          <template #activator="{props: tip}">
            <button v-bind="tip" type="button" class="bl-item" :aria-label="l.sentence" @click="onEntry(l)">
              <slot name="swatch" :entry="l"><span v-if="l.chip" class="bl-chip" :class="'bl-chip--' + l.chip"><v-icon :icon="l.icon" size="13"/></span><booking-cell swatch v-else :model="{cls: l.cls, icon: l.icon ?? null}"/></slot>
              <span class="bl-word">{{ l.word }}</span>
            </button>
          </template>
        </v-tooltip>
      </template>
      <button type="button" class="bl-help" @click="show()">
        <v-icon size="14" icon="mdi-information-outline"/><span>{{ LEGEND_HELP }}</span>
      </button>
    </template>
    <tl-icon-btn v-else :tooltip="title" icon="mdi-information-outline" :icon-size="18" location="bottom" @click="show()"/>

    <tl-dialog v-model="open" :title="title" :max-width="440">
      <div class="bl-list">
        <div v-for="l in items" :key="l.key" class="bl-row">
          <slot name="swatch" :entry="l"><span v-if="l.chip" class="bl-chip" :class="'bl-chip--' + l.chip"><v-icon :icon="l.icon" size="13"/></span><booking-cell swatch v-else :model="{cls: l.cls, icon: l.icon ?? null}"/></slot>
          <div class="bl-row-text">
            <div class="bl-row-word">{{ l.word }}</div>
            <div class="bl-row-sentence">{{ afterWord(l) }}</div>
          </div>
        </div>
      </div>
      <template #actions>
        <v-btn color="primary" variant="flat" rounded="pill" @click="open = false">Got it</v-btn>
      </template>
    </tl-dialog>
  </div>
</template>

<style scoped lang="scss">
.bl { font-size: 12px; color: rgb(var(--v-theme-charcoal)); }
.bl--inline { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 10px; }
/* X91: the corner button, centred in the grid's top-left cell. */
.bl--corner { display: flex; align-items: center; justify-content: center; }
.bl-item {
  display: inline-flex; align-items: center; gap: 6px; padding: 3px 4px; border-radius: 6px;
  background: none; border: none; font: inherit; font-weight: 600; color: inherit; cursor: default;
  &:hover { background: rgb(var(--v-theme-light)); }
}
.bl-help {
  display: inline-flex; align-items: center; gap: 4px; padding: 3px 6px; border-radius: 6px;
  background: none; border: none; font: inherit; font-size: 11.5px; font-weight: 600; color: rgb(var(--v-theme-primary)); cursor: pointer;
  .v-icon { color: rgb(var(--v-theme-primary)); }
  &:hover { background: rgb(var(--v-theme-brandTint)); }
}
/* X89 (R33 F4): a chip entry never shrinks beside a long sentence, so its words line up with the swatch rows'. */
.bl-chip { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 14px; flex: 0 0 auto; }
.bl-row .bl-chip { width: 36px; height: 24px; margin-top: 1px; }
.bl-chip--brand { color: rgb(var(--v-theme-primary)); }
.bl-chip--warn { color: rgb(var(--v-theme-warning)); }
.bl-list { display: flex; flex-direction: column; gap: 2px; }
.bl-row { display: flex; align-items: flex-start; gap: 12px; padding: 9px 2px; border-top: 1px solid rgb(var(--v-theme-borderLight)); scroll-margin-top: 8px; &:first-child { border-top: none; } }
.bl-row :deep(.bwc--swatch) { width: 36px; height: 24px; margin-top: 1px; }
.bl-row-text { flex: 1 1 auto; min-width: 0; }
.bl-row-word { font-size: 13px; font-weight: 700; }
.bl-row-sentence { font-size: 12.5px; color: rgb(var(--v-theme-charcoal)); line-height: 1.45; margin-top: 1px; }
</style>
